import React, { ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { PlayerInfoWithProjectionsFragment, PlayerProjectionFragment } from '@/api/events/query.generated';
import { EventInfo } from '@/api/events/types/types';
import { LeftSideTabBar } from '@/components/LeftSideTabBar';
import { LineSeparator } from '@/components/LineSeparator';
import { Loading } from '@/components/Loading';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { BetrAnalytics } from '@/feature/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { PlayerWithTeam, PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { eventUtils } from '@/feature/betslip-pickem/utils/event-utils';
import { createPlayerEventKey } from '@/feature/betslip-pickem/utils/markets-cache-store-utils';
import { UfcFightRow } from '@/feature/fantasy-event-sheets/components/ufc-events/UfcFightRow';
import { MatchPreviewResponse } from '@/feature/fantasy-event-sheets/hooks/use-match-preview';
import { DEFAULT_EVENT_CARD_LIST_HEIGHT, EventCardsList } from '@/feature/lobby/components/EventCardsList';
import { PlayerItem } from '@/feature/lobby/components/PlayerItem';
import { PlayersFilters } from '@/feature/lobby/components/PlayersFilters';
import { PlayerTileRow } from '@/feature/lobby/components/tile/PlayerTileRow';
import { usePickSelection } from '@/feature/lobby/hooks/use-pick-selection';
import {
    FILTER_MODES_TABS,
    FilterType,
    Filters,
    MATCH_PREVIEW_TAB,
    PLAYERS_TAB,
    PROJECTIONS_TAB,
    SOCIAL_LINEUPS_TAB,
    UFC_FIGHTS_TAB,
    UFC_FILTER_MODES_TABS,
    isFilterBarHidden,
} from '@/feature/lobby/utils/filters';
import { POPULAR_PLAYERS_FILTER } from '@/feature/lobby/utils/players';
import { useOpenPlayerProfile } from '@/feature/player-profile-sheets/hooks/use-open-player-profile-sheet';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { SocialFeedCardsRow } from '@/feature/social-feed/components/SocialFeedCardsRow';
import { useSocialFeedEnabled } from '@/feature/social-feed/hooks/use-social-feed-enabled';
import { LeaguePositionFilters } from '@/hooks/use-fantasy-league-configs';
import { common } from '@/styles/styles';
import { IndividualVersusEvent, League } from '@/types/api.generated';
import { SocialFeedEntry } from '@/types/socialFeed.generated';
import { isWeb } from '@/utils/constants-platform-specific';
import { nflFullSeason } from '@/utils/league';

import { DEDICATED_EVENT_CARD_LIST_HEIGHT, DedicatedEventCardsList } from '../components/DedicatedEventCardsList';
import { usePlayerFiltersActions, usePlayerFiltersState } from './use-player-filters';

const MIN_REQUIRED_POPULAR_PLAYERS = 2;

const FOOTER_SPACE = 22;
/**
 * Hook for exposing methods to render league specific content
 *     - renderPlayerItem - renders the player item based on the filter type
 *     - renderLeagueComponents - renders the league components like tabs, filters, events
 *     - renderLeagueFooter - renders the footer message based on the filter type
 *
 * Also used to render content inside the EventSheet
 *
 * @param league - the league for which the content is to be rendered
 * @param selectedFilter - Projection | Players
 * @param selectFilter - callback to change the filter method
 * @param sortedEvents - sorted events for the league
 * @param sortedPlayers - sorted players for the league
 * @param positionFilters - position filters configured for the league
 * @param eventsLoading - loading state of the events
 * @param hideGameTime - optional flag to hide game time in the player item
 * @param showMatchPreviewTab - optional flag to enable the match preview tab
 */
export const useLeagueContentRenderer = ({
    league,
    selectedFilter,
    selectFilter,
    sortedEvents,
    sortedPlayers,
    positionFilters,
    socialFeedEntries,
    matchPreviewData,
    eventsLoading,
    socialLineupsLoading,
    matchPreviewLoading,
    hideGameTime,
    openSinglePlayerProfile,
    popularProjectionsPlayers,
    showMatchPreviewTab,
}: {
    league: League;
    selectedFilter: FilterType;
    selectFilter: (filter: FilterType) => void;
    sortedPlayers: PlayerWithTeamAndEvent[];
    sortedEvents: EventInfo[];
    positionFilters: LeaguePositionFilters;
    socialFeedEntries: SocialFeedEntry[] | undefined;
    matchPreviewData?: MatchPreviewResponse;
    eventsLoading: boolean;
    socialLineupsLoading: boolean;
    matchPreviewLoading?: boolean;
    hideGameTime?: boolean;
    openSinglePlayerProfile?: (player: PlayerWithTeamAndEvent, playerProjectionFilter?: string) => void;
    popularProjectionsPlayers?: PlayerWithTeam[];
    /** Adds the match preview tab last. Only used on the event details sheet, not in the lobby. */
    showMatchPreviewTab?: boolean;
}) => {
    const { playerProjectionFilter } = usePlayerFiltersState();
    const { setPlayerPositionFilter, setPlayerProjectionFilter, resetProjectionsFilter } = usePlayerFiltersActions();

    const { makeSelection, removeSelection } = usePickSelection();

    const { t } = useTranslation('betslip_pickem');

    const hasPositionFilters = positionFilters && positionFilters.length > 0;
    const filterIsHidden = isFilterBarHidden(selectedFilter, hasPositionFilters);

    const socialFeedEnabled = useSocialFeedEnabled();

    const filterModes = useMemo(() => {
        const tabs =
            league === League.Ufc
                ? socialFeedEnabled
                    ? [PROJECTIONS_TAB, SOCIAL_LINEUPS_TAB, UFC_FIGHTS_TAB]
                    : UFC_FILTER_MODES_TABS
                : socialFeedEnabled
                ? [PROJECTIONS_TAB, SOCIAL_LINEUPS_TAB, PLAYERS_TAB]
                : FILTER_MODES_TABS;
        return showMatchPreviewTab ? [...tabs, MATCH_PREVIEW_TAB] : tabs;
    }, [league, socialFeedEnabled, showMatchPreviewTab]);

    const allUniqueProjections = useMemo(() => {
        const allPlayers = sortedEvents?.reduce((acc: PlayerInfoWithProjectionsFragment[], event) => {
            return [...acc, ...eventUtils.getAllPlayers(event)];
        }, []);

        const allProjections = allPlayers.reduce(
            (acc: PlayerProjectionFragment[], player: PlayerInfoWithProjectionsFragment) => [
                ...acc,
                ...player.projections,
            ],
            []
        );
        const uniqueProjections = [...new Map(allProjections.map(item => [item.key, item])).values()];

        return uniqueProjections.sort((proj1, proj2) => proj1.order - proj2.order);
    }, [sortedEvents]);

    const projectionFiltersData = useMemo(() => {
        const popularPlayersFilter = {
            key: POPULAR_PLAYERS_FILTER,
            label: t('popular'),
        } as PlayerProjectionFragment;

        if (popularProjectionsPlayers === undefined) {
            return [];
        }

        return popularProjectionsPlayers.length >= MIN_REQUIRED_POPULAR_PLAYERS
            ? [popularPlayersFilter, ...allUniqueProjections]
            : allUniqueProjections;
    }, [allUniqueProjections, popularProjectionsPlayers, t]);

    const setTab = useCallback(
        (tabId: string) => {
            const newFilterType = filterModes.find(tab => tab.id === tabId)?.filterType ?? filterModes[0].filterType;

            // Set default position filter synchronously to avoid an extra render cycle
            // from PlayerPositionsFilters useEffect setting it after mount
            if (newFilterType === Filters.Position) {
                const firstFilter = positionFilters?.[0];
                if (firstFilter) {
                    setPlayerPositionFilter(firstFilter.position ?? '', firstFilter.positionDescription ?? '');
                }
            }
            // Set default projection filter synchronously to avoid an extra render cycle
            // from PlayerProjectionsFilters useEffect setting it after mount
            if (newFilterType === Filters.Projection) {
                const firstProjection = projectionFiltersData[0];
                if (firstProjection) {
                    setPlayerProjectionFilter(firstProjection.key, firstProjection.label ?? '');
                }
            } else {
                resetProjectionsFilter();
            }

            BetrAnalytics.trackEvent(getAnalyticsEventForFilterType(newFilterType), { league: league });

            selectFilter(newFilterType);
        },
        [
            filterModes,
            league,
            selectFilter,
            positionFilters,
            setPlayerPositionFilter,
            projectionFiltersData,
            setPlayerProjectionFilter,
            resetProjectionsFilter,
        ]
    );

    const renderLeagueFooter = useCallback(() => {
        const eventsCount = sortedEvents.length;
        //Render loading while we still render the initial filters, and make select a projection filter
        const filtersLoading =
            eventsCount > 0 && selectedFilter === Filters.Projection && playerProjectionFilter === undefined;

        const socialFeedLoading = selectedFilter === Filters.SocialLineups && socialLineupsLoading;

        const matchPreviewDataLoading = selectedFilter === Filters.Preview && matchPreviewLoading;

        const loading = eventsLoading || filtersLoading || socialFeedLoading || matchPreviewDataLoading;

        if (loading) {
            return (
                <Box m={'s46'}>
                    <Loading />
                </Box>
            );
        }

        const showingPlayers = selectedFilter === Filters.Position || selectedFilter === Filters.Projection;
        const showingSocialLineups = selectedFilter === Filters.SocialLineups;
        const showingMatchPreview = selectedFilter === Filters.Preview;

        if (matchPreviewData === undefined && showingMatchPreview) {
            BetrAnalytics.trackEvent(AnalyticsEvent.FANTASY_MATCH_PREVIEW_ERROR, { league: league });
            return (
                <Box py={'s32'}>
                    <Text fontWeight="bold" textAlign={'center'} testID={'no-players-available'}>
                        No match preview available now.
                    </Text>
                </Box>
            );
        }
        if (sortedPlayers.length === 0 && showingPlayers) {
            return (
                <Box py={'s16'}>
                    <Text fontWeight="bold" textAlign={'center'} testID={'no-players-available'}>
                        {t('no_projection_available')}
                    </Text>
                </Box>
            );
        }

        if (socialFeedEntries?.length === 0 && showingSocialLineups) {
            return (
                <Box py={'s32'}>
                    <Text fontWeight="bold" textAlign={'center'} testID={'no-players-available'}>
                        Lineups will be available soon.
                    </Text>
                </Box>
            );
        }
        return <SizedBox value={FOOTER_SPACE} />;
    }, [
        sortedEvents.length,
        selectedFilter,
        playerProjectionFilter,
        socialLineupsLoading,
        matchPreviewLoading,
        eventsLoading,
        matchPreviewData,
        sortedPlayers.length,
        socialFeedEntries?.length,
        league,
        t,
    ]);

    const renderFilters = useCallback(() => {
        const initialIndex = filterModes.findIndex(tab => tab.filterType === selectedFilter);
        return (
            <>
                <LeftSideTabBar
                    tabs={filterModes}
                    initialIndex={initialIndex}
                    setSelectedTab={setTab}
                    location="event"
                />
                <PlayersFilters
                    league={league}
                    filterType={selectedFilter}
                    projectionFiltersData={projectionFiltersData}
                    positionFilters={positionFilters}
                />
            </>
        );
    }, [filterModes, league, selectedFilter, setTab, projectionFiltersData, positionFilters]);

    const renderEventCards = useCallback(() => {
        if (league === League.Ufc || nflFullSeason(league)) {
            //There are no event cards for non team events
            return null;
        }
        if (league === League.Pga) {
            return (
                <MaxWidthWrapper minHeight={DEDICATED_EVENT_CARD_LIST_HEIGHT}>
                    <DedicatedEventCardsList sortedEvents={sortedEvents} loading={eventsLoading} />
                </MaxWidthWrapper>
            );
        }
        return (
            <MaxWidthWrapper minHeight={DEFAULT_EVENT_CARD_LIST_HEIGHT} key={`event-cards-${league}`}>
                <EventCardsList sortedEvents={sortedEvents} loading={eventsLoading} />
            </MaxWidthWrapper>
        );
    }, [eventsLoading, league, sortedEvents]);

    const { openPlayerProfile } = useOpenPlayerProfile(sortedPlayers);

    const openPlayerProfileWrapper = useCallback(
        (playerId: string, eventId: string) => {
            // on web we redirect user to player profile, not the single player profile sheet
            if (!isWeb && openSinglePlayerProfile) {
                const foundPlayer = sortedPlayers.find(player => player.id === playerId);
                if (!foundPlayer) {
                    return;
                }
                openSinglePlayerProfile(foundPlayer, playerProjectionFilter);
                return;
            }
            openPlayerProfile({ playerId, eventId, playerProjectionFilter });
        },
        [sortedPlayers, openPlayerProfile, openSinglePlayerProfile, playerProjectionFilter]
    );

    const renderPlayerItem = useCallback(
        ({
            item,
            index,
            ignoreIndexes = 0,
        }: {
            item: PlayerWithTeamAndEvent;
            index: number;
            ignoreIndexes?: number;
        }): ReactElement | null => {
            const { event: eventInfo, ...playerWithoutEvent } = item;
            const adjustedIndex = index - ignoreIndexes;
            const isFirst = adjustedIndex === 0;
            const firstItemPaddingTop = isFirst && filterIsHidden ? 's16' : undefined;

            if (Filters.Projection === selectedFilter) {
                return (
                    <Box paddingTop={firstItemPaddingTop}>
                        <PlayerTileRow
                            key={`player-${createPlayerEventKey(
                                item.id,
                                eventInfo.id
                            )}-${playerProjectionFilter}-${index}`}
                            openPlayerProfile={openPlayerProfileWrapper}
                            makeSelection={makeSelection}
                            removeSelection={removeSelection}
                            analyticsTag={'ProjectionsTab'}
                            index={adjustedIndex}
                            sortedPlayers={sortedPlayers}
                            hideGameTime={hideGameTime}
                        />
                    </Box>
                );
            }

            return (
                <MaxWidthWrapper>
                    <Box pl={'s14'} pr={'s16'} paddingTop={firstItemPaddingTop}>
                        <PlayerItem
                            eventInfo={eventInfo}
                            player={playerWithoutEvent}
                            key={`player-${createPlayerEventKey(playerWithoutEvent.id, eventInfo.id)}-${index}`}
                            filter={selectedFilter}
                            testID={playerWithoutEvent.id}
                            makeSelection={makeSelection}
                            removeSelection={removeSelection}
                            analyticsTag={'PlayersTab'}
                            hideGameTime={hideGameTime}
                            openPlayerProfile={openPlayerProfileWrapper}
                        />
                    </Box>
                    <LineSeparator style={[common.separator, styles.marginHorizontal]} />
                </MaxWidthWrapper>
            );
        },
        [
            selectedFilter,
            sortedPlayers,
            makeSelection,
            removeSelection,
            hideGameTime,
            openPlayerProfileWrapper,
            playerProjectionFilter,
            filterIsHidden,
        ]
    );

    const renderUFCFightRow = useCallback(
        (event: IndividualVersusEvent) => {
            return <UfcFightRow eventInfo={event} openPlayerProfile={openPlayerProfileWrapper} />;
        },
        [openPlayerProfileWrapper]
    );

    const renderSocialFeedItem = useCallback(
        ({
            item,
            index,
            ignoreIndexes = 0,
        }: {
            item: SocialFeedEntry;
            index: number;
            ignoreIndexes?: number;
        }): ReactElement | null => {
            const adjustedIndex = index - ignoreIndexes;
            const isFirst = adjustedIndex === 0;
            return (
                <Box paddingTop={isFirst ? 's24' : 's16'}>
                    <SocialFeedCardsRow
                        index={adjustedIndex}
                        items={socialFeedEntries ?? []}
                        key={`social-feed-${item.socialFeedEntryId}-${index}`}
                    />
                </Box>
            );
        },
        [socialFeedEntries]
    );

    return {
        renderPlayerItem,
        renderUFCFightRow,
        renderFilters,
        renderEventCards,
        renderLeagueFooter,
        renderSocialFeedItem,
        filterIsHidden,
    };
};

const getAnalyticsEventForFilterType = (filterType: FilterType): AnalyticsEvent => {
    const map: Record<FilterType, AnalyticsEvent> = {
        [Filters.Projection]: AnalyticsEvent.FANTASY_EVENT_TAB_PROJECTIONS,
        [Filters.SocialLineups]: AnalyticsEvent.FANTASY_EVENT_TAB_LINEUPS,
        [Filters.Position]: AnalyticsEvent.FANTASY_EVENT_TAB_PLAYERS,
        [Filters.Preview]: AnalyticsEvent.FANTASY_EVENT_TAB_PREVIEW,
    };
    return map[filterType];
};

const styles = StyleSheet.create({
    marginHorizontal: {
        marginHorizontal: 16,
    },
});
